import React, { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { CircularProgress, Stack, Typography } from '@mui/material'
import {
  completeEmbeddedAiToolPayment,
  getAiToolPaymentErrorMessage,
  getPendingAiToolPayment,
  isAiToolPaymentFailureStatus,
  resolveAiToolFormPath,
} from '../utils/aiTool/payment'

const AiToolPaymentReturn = () => {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const [message, setMessage] = useState('Processing your payment...')

  useEffect(() => {
    let cancelled = false

    const finish = async () => {
      const paymentId = searchParams.get('paymentId')
      const returnPath =
        searchParams.get('returnPath') ||
        getPendingAiToolPayment(paymentId)?.returnPath ||
        '/'
      const status = searchParams.get('status')
      const toolType = searchParams.get('toolType')

      if (isAiToolPaymentFailureStatus(status)) {
        const formPath = resolveAiToolFormPath({
          toolType,
          returnPath,
          paymentId,
        })

        navigate(
          `${formPath}?paymentError=${encodeURIComponent(
            getAiToolPaymentErrorMessage(searchParams),
          )}${paymentId ? `&paymentId=${encodeURIComponent(paymentId)}` : ''}`,
          { replace: true },
        )
        return
      }

      try {
        if (cancelled) return

        if (!paymentId) {
          throw new Error('Payment session expired. Please try again.')
        }

        setMessage('Running analysis...')

        const { returnPath: storedReturnPath } =
          await completeEmbeddedAiToolPayment({
            paymentId,
            toolType,
          })

        if (cancelled) return

        navigate(`${storedReturnPath || returnPath}?paymentSuccess=1`, {
          replace: true,
        })
      } catch (error) {
        if (cancelled) return

        const formPath = resolveAiToolFormPath({
          toolType,
          returnPath,
          paymentId,
        })

        navigate(
          `${formPath}?paymentError=${encodeURIComponent(
            error?.response?.data?.message ||
              error.message ||
              'Payment processing failed',
          )}${paymentId ? `&paymentId=${encodeURIComponent(paymentId)}` : ''}`,
          { replace: true },
        )
      }
    }

    finish()

    return () => {
      cancelled = true
    }
  }, [navigate, searchParams])

  return (
    <Stack
      alignItems="center"
      justifyContent="center"
      spacing={2}
      sx={{ minHeight: '60vh', px: 2 }}
    >
      <CircularProgress />
      <Typography variant="body1">{message}</Typography>
    </Stack>
  )
}

export default AiToolPaymentReturn
